// src/routes/AppRoutes.jsx
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";

// Pages
import HomePage from "../pages/HomePage";
import LoginPage from "../pages/LoginPage";
import RegisterPage from "../pages/RegisterPage";
import GrievancePage from "../pages/GrievancePage";
import UserGrievancesPage from "../pages/UserGrievancesPage";
import AllGrievancesPage from "../pages/AllGrievancesPage";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage";
import TermsPage from "../pages/TermsPage";
import ProfilePage from "../pages/ProfilePage";
import ForumPage from "../Forum/ForumPage";
import ForumMessages from "../Forum/ForumMessages";
import ActionsPage from "../pages/ActionsPage";
import ContactPage from "../pages/ContactPage";
import AdminDashboard from "../pages/AdminDashboard";
import DonationPage from "../pages/DonationPage";
import ForgotPassword from "../pages/ForgotPassword";
import ResetPassword from "../pages/ResetPassword";
import LetterGenerator from "../pages/LetterGenerator";
import NotFoundPage from "../pages/NotFoundPage";
import ReservationPage from "../pages/ReservationPage";
import Adhesion from "../pages/Adhesion";
import VideosPage from "../pages/VideosPage";
import ChatIAPage from "../pages/ChatIAPage";
import SocialPage from "../pages/SocialPage"; // ✅ chemin corrigé

// Components utilisés comme pages
import ValidateEmailPage from "../lib/ValidateEmailPage";
import Newsletter from "../components/admin/Newsletter";
import LiveStreamManager from "../components/admin/LiveStreamManager";
import LiveStreamPage from "../components/live/NonoChannelLive";
import Direct from "../components/live/Direct";
import AssociationStory from "../components/home/AssociationStory";
import TenantSpacePage from "../pages/tenant/TenantSpacePage";
// Noël
import SantaPublicChat from "../components/noel/SantaPublicChat";
import SantaPremiumPage from "../components/noel/SantaPremiumPage";
import SantaPlanPaymentPage from "../components/noel/SantaPlanPaymentPage";

/* ---------- Routes protégées ---------- */

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated || user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
}

function RoleProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated || !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/* ---------- Index des routes ---------- */

const AppRoutes = () => {
  return (
    <Routes>
      {/* --- Public Routes --- */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/adhesion" element={<Adhesion />} />
      <Route path="/validate-email/:token" element={<ValidateEmailPage />} />
      <Route path="/videos" element={<VideosPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/donation" element={<DonationPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/letter-generator" element={<LetterGenerator />} />
      <Route path="/direct" element={<Direct />} />
      <Route path="/association-story" element={<AssociationStory />} />

      {/* 🎅 Chat public Père Noël */}
      <Route path="/pere-noel" element={<SantaPublicChat />} />
      {/* 🎅 Page Premium Père Noël */}
      <Route path="/pere-noel/premium" element={<SantaPremiumPage />} />
      {/* 💳 Paiement d’un plan précis */}
      <Route
        path="/pere-noel/premium/:planId"
        element={<SantaPlanPaymentPage />}
      />

      {/* --- Member / Protected Routes --- */}

      {/* 🔹 Réseau social NMS (protégé) */}
      <Route
        path="/reseau"
        element={
          <ProtectedRoute>
            <SocialPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/grievances"
        element={
          <RoleProtectedRoute allowedRoles={["user", "admin", "member"]}>
            <GrievancePage />
          </RoleProtectedRoute>
        }
      />
  <Route
    path="/tenant-space"
    element={
      <RoleProtectedRoute allowedRoles={["tenant", "admin"]}>
        <TenantSpacePage />
      </RoleProtectedRoute>
    }
  />

      <Route
        path="/my-grievances"
        element={
          <ProtectedRoute>
            <UserGrievancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-grievances"
        element={
          <ProtectedRoute>
            <AllGrievancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-ia"
        element={
          <ProtectedRoute>
            <ChatIAPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/actions"
        element={
          <ProtectedRoute>
            <ActionsPage />
          </ProtectedRoute>
        }
      />

      {/* Forum */}
      <Route
        path="/forum"
        element={
          <ProtectedRoute>
            <ForumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forums/:themeId"
        element={
          <ProtectedRoute>
            <ForumMessages />
          </ProtectedRoute>
        }
      />

      {/* Réservation */}
      <Route
        path="/reservation"
        element={
          <ProtectedRoute>
            <ReservationPage />
          </ProtectedRoute>
        }
      />

      {/* Live */}
      <Route
        path="/live-stream"
        element={
          <ProtectedRoute>
            <LiveStreamPage />
          </ProtectedRoute>
        }
      />

      {/* --- Admin Routes --- */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/newsletter"
        element={
          <AdminRoute>
            <Newsletter />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/livestream"
        element={
          <AdminRoute>
            <LiveStreamManager />
          </AdminRoute>
        }
      />

      {/* 404 Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
